import { FieldKey } from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { FormValues } from '@core/data-access/models/form/form.model';
import { Person, PersonType, PersonUuid } from '@core/data-access/models/person.model';
import { DynamicFieldOptionsTranslationFactory } from '@core/data-access/services/dynamic-form-field/dynamic-field-options-translation-factory.service';
import { mapResponseValueToFormFields } from '@core/utils/form-helpers';
import { TranslateService } from '@ngx-translate/core';
import { getUUID } from '@shared/app-utils';
import { defaultTo, forEach, includes, isEmpty, keys, omit, reduce } from 'lodash';
import {
  CreateSavedTicketHolderRequest,
  CreateSavedTicketHoldersResponse,
  GetSavedTicketHoldersResponse,
  UpdateSavedTicketHolderRequest
} from '../models/saved-ticket-holders-api.model';
import { SetSavedTicketHolders } from '../models/saved-ticket-holders.store.model';

export const mapGetSavedTicketHoldersResponseToSetSavedTicketHolders = (
  getSavedTicketHoldersResponse: GetSavedTicketHoldersResponse,
  translateService: TranslateService,
  dynamicFieldOptionsTranslationFactory: DynamicFieldOptionsTranslationFactory
) => {
  const setSavedTicketHolders: SetSavedTicketHolders = {};

  forEach(getSavedTicketHoldersResponse, (savedTicketHolderResponse) => {
    const { personId, personType } = savedTicketHolderResponse;

    const formFields = reduce(
      keys(omit(savedTicketHolderResponse, ['personId', 'personType', 'ticketPersonId', 'personUuid'])),
      (formFields, fieldKey: FieldKey) =>
        mapResponseValueToFormFields(
          formFields,
          fieldKey,
          savedTicketHolderResponse[fieldKey],
          translateService,
          dynamicFieldOptionsTranslationFactory
        ),
      {}
    );

    const personUuid = getUUID();
    const person: Person = {
      id: personId,
      uuid: personUuid,
      type: personType
    };

    setSavedTicketHolders[personUuid] = {
      person,
      formFields
    };
  });

  return setSavedTicketHolders;
};

export const mapCreatedTicketHolderResponseToSetSavedTicketHolders = (
  createdSavedTicketHoldersResponse: CreateSavedTicketHoldersResponse,
  translateService: TranslateService,
  dynamicFieldOptionsTranslationFactory: DynamicFieldOptionsTranslationFactory
) => {
  const setSavedTicketHolders: SetSavedTicketHolders = {};

  forEach(createdSavedTicketHoldersResponse, (createdSavedTicketHolderResponse) => {
    const { personId, personType, validationError } = createdSavedTicketHolderResponse;

    if (!isEmpty(validationError)) {
      return;
    }

    const formFields = reduce(
      keys(
        omit(createdSavedTicketHolderResponse, [
          'personId',
          'personType',
          'ticketPersonId',
          'personUuid',
          'validationError'
        ])
      ),
      (formFields, fieldKey: FieldKey) =>
        mapResponseValueToFormFields(
          formFields,
          fieldKey,
          createdSavedTicketHolderResponse[fieldKey],
          translateService,
          dynamicFieldOptionsTranslationFactory
        ),
      {}
    );

    const personUuid = getUUID();
    const person: Person = {
      id: personId,
      uuid: personUuid,
      type: personType
    };

    setSavedTicketHolders[personUuid] = {
      person,
      formFields
    };
  });

  return setSavedTicketHolders;
};

export const mapCreatedTicketHoldersResponseToSetSavedTicketHolders = (
  createdSavedTicketHoldersResponse: CreateSavedTicketHoldersResponse,
  translateService: TranslateService,
  dynamicFieldOptionsTranslationFactory: DynamicFieldOptionsTranslationFactory
) => {
  const setSavedTicketHolders: SetSavedTicketHolders = {};

  forEach(createdSavedTicketHoldersResponse, (createdSavedTicketHolderResponse) => {
    const { personId, personUuid, personType, validationError } = createdSavedTicketHolderResponse;

    if (!isEmpty(validationError)) {
      return;
    }

    const formFields = reduce(
      keys(
        omit(createdSavedTicketHolderResponse, [
          'personId',
          'personType',
          'ticketPersonId',
          'personUuid',
          'validationError'
        ])
      ),
      (formFields, fieldKey: FieldKey) =>
        mapResponseValueToFormFields(
          formFields,
          fieldKey,
          createdSavedTicketHolderResponse[fieldKey],
          translateService,
          dynamicFieldOptionsTranslationFactory
        ),
      {}
    );

    const person: Person = {
      id: personId,
      uuid: personUuid,
      type: personType
    };

    setSavedTicketHolders[personUuid] = {
      person,
      formFields
    };
  });

  return setSavedTicketHolders;
};

export const mapUpdateTicketHolderMergedValuesToFormFields = (
  mergedValues: UpdateSavedTicketHolderRequest,
  skipFields: FieldKey[] = [FieldKey.GDPR, FieldKey.CV]
) =>
  reduce(
    keys(mergedValues),
    (formFields, fieldKey: FieldKey) => {
      if (includes(skipFields, fieldKey)) {
        return formFields;
      }

      formFields[fieldKey] = {
        value: mergedValues[fieldKey],
        isQuestionnaire: false,
        isSaved: true
      };

      return formFields;
    },
    {}
  );

export const mapFormValuesToCreateTicketHolderRequest = (
  personType: PersonType,
  formValues: FormValues,
  personUuid: PersonUuid = null,
  ticketPersonId: number = null
): CreateSavedTicketHolderRequest | UpdateSavedTicketHolderRequest => ({
  personType,
  personUuid,
  ticketPersonId,
  gender: defaultTo(formValues[FieldKey.Gender] as string, ''),
  title: defaultTo(formValues[FieldKey.Title] as string, ''),
  firstName: defaultTo(formValues[FieldKey.FirstName] as string, ''),
  lastName: defaultTo(formValues[FieldKey.LastName] as string, ''),
  dateOfBirth: defaultTo(formValues[FieldKey.DateOfBirth] as string, ''),
  street: defaultTo(formValues[FieldKey.Street] as string, ''),
  zipCode: defaultTo(formValues[FieldKey.ZipCode] as string, ''),
  city: defaultTo(formValues[FieldKey.City] as string, ''),
  company: defaultTo(formValues[FieldKey.Company] as string, ''),
  function: defaultTo(formValues[FieldKey.Function] as string, ''),
  department: defaultTo(formValues[FieldKey.Department] as string, ''),
  occupationalGroup: defaultTo(formValues[FieldKey.OccupationalGroup] as string, ''),
  telephone: defaultTo(formValues[FieldKey.Telephone] as string, ''),
  country: defaultTo(formValues[FieldKey.Country] as string, ''),
  email: defaultTo(formValues[FieldKey.Email] as string, ''),
  vatNumber: defaultTo(formValues[FieldKey.VatNumber] as string, '')
});

export const mapFormValuesToUpdateTicketHolderRequest = (
  personType: PersonType,
  formValues: FormValues
): CreateSavedTicketHolderRequest | UpdateSavedTicketHolderRequest => ({
  personType,
  gender: defaultTo(formValues[FieldKey.Gender] as string, ''),
  title: defaultTo(formValues[FieldKey.Title] as string, ''),
  firstName: defaultTo(formValues[FieldKey.FirstName] as string, ''),
  lastName: defaultTo(formValues[FieldKey.LastName] as string, ''),
  dateOfBirth: defaultTo(formValues[FieldKey.DateOfBirth] as string, ''),
  street: defaultTo(formValues[FieldKey.Street] as string, ''),
  zipCode: defaultTo(formValues[FieldKey.ZipCode] as string, ''),
  city: defaultTo(formValues[FieldKey.City] as string, ''),
  company: defaultTo(formValues[FieldKey.Company] as string, ''),
  function: defaultTo(formValues[FieldKey.Function] as string, ''),
  department: defaultTo(formValues[FieldKey.Department] as string, ''),
  occupationalGroup: defaultTo(formValues[FieldKey.OccupationalGroup] as string, ''),
  telephone: defaultTo(formValues[FieldKey.Telephone] as string, ''),
  country: defaultTo(formValues[FieldKey.Country] as string, ''),
  email: defaultTo(formValues[FieldKey.Email] as string, ''),
  vatNumber: defaultTo(formValues[FieldKey.VatNumber] as string, '')
});
